import React from "react";

function ProductCard({
  accent,
  product,
  onClick,
  showComparison = true,
  retailerLabel,
}) {
  const normalizePlatformKey = (platform) => {
    const p = String(platform || "").trim().toLowerCase();
    if (p === "morrisons") return "morrison";
    if (p === "sainsburry") return "sainsbury";
    if (p === "sainsburys") return "sainsbury";
    return p;
  };
  const parsePrice = (value) => {
    if (value == null) return null;
    if (typeof value === "number") return Number.isFinite(value) ? value : null;
    const normalized = String(value).trim().replace(",", ".").replace(/[^0-9.\-]/g, "");
    if (!normalized) return null;
    const parsed = Number.parseFloat(normalized);
    return Number.isFinite(parsed) ? parsed : null;
  };

  const accentClasses = {
    orange: "border-orange-400 text-orange-500",
    blue: "border-sky-400 text-sky-500",
    green: "border-emerald-400 text-emerald-500",
    purple: "border-purple-400 text-purple-500",
  };
  const retailerPillClasses = {
    Tesco: "bg-blue-50 text-blue-600",
    Morrisons: "bg-fuchsia-50 text-fuchsia-600",
    ASDA: "bg-emerald-50 text-emerald-600",
    "Sainsbury's": "bg-orange-50 text-orange-600",
  };

  const accentClass = accentClasses[accent || "orange"];
  const parsedPrice = parsePrice(product?.price);
  const priceText = parsedPrice == null ? "N/A" : parsedPrice.toFixed(2);
  const formatDisplayPrice = (value) => (value === "N/A" ? value : `£${value}`);

  const platformMeta = {
    tesco: { label: "Tesco", color: "#1359EF" },
    morrison: { label: "Morrisons", color: "#A20886" },
    asda: { label: "Asda", color: "#08A237" },
    sainsbury: { label: "Sainsbury's", color: "#EC570D" },
  };
  const comparison = (
    Array.isArray(product?.comparison) && product.comparison.length > 0
      ? product.comparison
      : ["tesco", "morrison", "asda", "sainsbury"].map((platform) => ({
          platform,
          price: null,
        }))
  ).map((item) => {
    const platform = normalizePlatformKey(item?.platform);
    const meta = platformMeta[platform] || {
      label: item?.platform || "Store",
      color: "#94A3B8",
    };
    const numericPrice = parsePrice(item?.price);
    return {
      store: meta.label,
      price: Number.isFinite(numericPrice) ? numericPrice.toFixed(2) : "N/A",
      colorHex: meta.color,
      numericPrice: Number.isFinite(numericPrice) ? numericPrice : null,
    };
  });
  const maxComparisonPrice = comparison.reduce(
    (max, item) => (item.numericPrice != null ? Math.max(max, item.numericPrice) : max),
    0
  );

  return (
    <button
      type="button"
      onClick={onClick}
      className="flex flex-col rounded-[18px] border border-slate-200 bg-white text-left shadow-sm transition-shadow hover:shadow-md"
    >
      <div className="flex-1 p-4">
        {/* Pack size pill at top-left */}
        <div className="mb-2 flex justify-start">
          <span className="inline-flex rounded-full bg-orange-50 px-2 py-0.5 text-[10px] font-semibold text-orange-600">
            {product?.packsize || "200g"}
          </span>
        </div>

        {/* Image */}
        <div className="mb-4 flex h-28 items-center justify-center rounded-xl bg-slate-100">
          {product?.images?.[0] ? (
            <img
              src={product.images[0]}
              alt={product.title || product.sku}
              className="h-full object-contain"
            />
          ) : (
            <span className="text-xs text-slate-400">No Image</span>
          )}
        </div>

        {/* Name */}
        <p className="text-sm font-semibold text-slate-900 line-clamp-2">
          {product?.title || product?.sku || "Easy Wring Turbo"}
        </p>

        {showComparison ? (
          <>
            {/* Best price pill */}
            <p className="mt-2 inline-flex rounded-full bg-emerald-50 px-3 py-1 text-[11px] font-semibold text-emerald-600">
              Best Price {formatDisplayPrice(priceText)}
            </p>

            {/* Comparison bar: prices row + colour bars + store labels */}
            <div className="mt-2 space-y-1 text-[10px]">
              {/* Prices row */}
              <div className="flex justify-between font-semibold text-slate-900">
                {comparison.map((item) => (
                  <span key={item.store}>{formatDisplayPrice(item.price)}</span>
                ))}
              </div>

              {/* Colour columns */}
              <div className="flex items-end justify-between gap-1">
                {comparison.map((item) => (
                  <div key={item.store} className="flex-1">
                    <div
                      className="mx-auto w-full rounded-t-md"
                      style={{
                        backgroundColor: item.colorHex,
                        height:
                          item.numericPrice == null || maxComparisonPrice <= 0
                            ? 10
                            : Math.max(10, Math.round((item.numericPrice / maxComparisonPrice) * 24)),
                      }}
                    />
                  </div>
                ))}
              </div>

              {/* Store labels row */}
              <div className="flex justify-between text-[9px] text-slate-600">
                {comparison.map((item) => (
                  <span key={item.store}>{item.store}</span>
                ))}
              </div>
            </div>
          </>
        ) : (
          <p
            className={`mt-2 inline-flex rounded-full px-3 py-1 text-[11px] font-semibold ${
              retailerPillClasses[retailerLabel] || "bg-emerald-50 text-emerald-600"
            }`}
          >
            {retailerLabel
              ? `${retailerLabel} Price ${formatDisplayPrice(priceText)}`
              : `Best Price ${formatDisplayPrice(priceText)}`}
          </p>
        )}
      </div>

      <div className="px-4 pb-4">
        <button
          type="button"
          className={`w-full rounded-full border py-2.5 text-xs font-semibold ${accentClass}`}
        >
          Add →
        </button>
      </div>
    </button>
  );
}

export default ProductCard;